import {
    CLASSNAME_BUTTON,
    CLASSNAME_HIGHLIGHT,
    CLASSNAME_LEGAL,
    CLASSNAME_PRICE,
    CLASSNAME_STAIRWAY,
    CLASSNAME_TARIFFINFO,
    OFFER_PRICE_INFORMATION_CLASSNAME,
} from '@vfde-brix/offer-price';
import { handleLegacyClickEventBindings } from '../container/Overlay/helper/handleLegacyClickEventBindings';
import mountJustText from './mountJustText';
import mountPromoPrice from './mountPromoPrice';
import mountButton from './mountButton';
import {
    CLASSNAME_HIDDEN,
    Price,
    removeUndefinedFieldsFromObject,
} from '@vfde-brix/core';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-fe/sails-constants';
import {
    getHeadline,
    getHighlightBadgeContent,
    getMonthlyPrice,
    getOneTimePrice,
    getPriceCondition,
    getPriceSizeVariation,
    getPrimaryButtonContent,
    getSubline,
    shouldHideMonthlyPrice,
    shouldHideTariffInformation,
    shouldHideVvlLink,
} from '../container/DeviceDetail/helpers/promoPriceHelper';
import composeLegalText, { composeStairwayText } from '../container/Tariff/helper/composeLegalText';
import mountHighlightBadge from './mountHightlightBadge';
import { HighlightBadge } from '@vfde-brix/highlight-badge';
import { DeviceDetailActionDispatchers } from 'app/container/DeviceDetail/actions';
import { OfferPriceProperties } from 'app/container/DeviceDetail/interfaces/api';
import { Offer, StateProps } from 'app/container/DeviceDetail/interfaces/state';

/**
 * Mount all offer price components
 * @param container
 */
const mountOfferPriceComponents = (container: HTMLElement): OfferPriceProperties => {
    const highlightBadgeContainer = container.getElementsByClassName(CLASSNAME_HIGHLIGHT)[0] as HTMLElement;
    const priceContainer = container.getElementsByClassName(CLASSNAME_PRICE)[0];
    const buttonContainer = container.getElementsByClassName(CLASSNAME_BUTTON)[0];
    const tariffInfoContainer = container.getElementsByClassName(CLASSNAME_TARIFFINFO)[0];

    const deviceName = mountJustText(tariffInfoContainer.firstElementChild as HTMLElement);
    const devicePrice = mountPromoPrice(priceContainer.firstElementChild as HTMLElement);
    const extrasButton = mountButton(buttonContainer.firstElementChild as HTMLElement);
    const tariffButton = mountButton(buttonContainer.lastElementChild as HTMLElement);
    const subline = mountJustText(tariffInfoContainer.lastElementChild as HTMLElement);
    const tariffPrice = mountPromoPrice(priceContainer.lastElementChild as HTMLElement);
    const legalText = mountJustText(container.getElementsByClassName(CLASSNAME_LEGAL)[0].firstElementChild as HTMLElement);
    const stairway = mountJustText(container.getElementsByClassName(CLASSNAME_STAIRWAY)[0].firstElementChild as HTMLElement);

    const informationElement = container.getElementsByClassName(OFFER_PRICE_INFORMATION_CLASSNAME)[0].firstElementChild as HTMLElement;
    const information = informationElement && mountJustText(informationElement);

    const offerPriceProperties: OfferPriceProperties = {
        tariffInfo: {
            headline: deviceName,
            subline,
        },
        promoPrice: {
            device: devicePrice,
            tariff: tariffPrice,
        },
        legalText,
        stairway,
        information,
        buttons: {
            primary: extrasButton,
            secondary: tariffButton,
        },
    };

    // If we have something in the highlight badge container, mount it
    if (highlightBadgeContainer?.children.length > 0) {
        offerPriceProperties.highlightBadge = mountHighlightBadge(highlightBadgeContainer);
    }

    return offerPriceProperties;
};

/**
 * Update device information in offer price
 * @param offerPrice
 * @param newState
 */
export const updateDeviceInformation = (offerPrice: OfferPriceProperties, newState: StateProps) => {
    const { name, price, subscriptionId, isHardwareOnly, hasInsurance, isTradeIn } = newState;
    offerPrice.tariffInfo.headline.update({ content: getHeadline(name!, { hasInsurance, isHardwareOnly, isTradeIn }) });
    offerPrice.promoPrice.device.update({
        price: Price.fromNumber(getOneTimePrice(price, isHardwareOnly)!),
        optSizeVariation: getPriceSizeVariation(!!subscriptionId, isHardwareOnly, hasInsurance),
        optPriceCondition: getPriceCondition(!!subscriptionId, isHardwareOnly),
    });
    if (offerPrice.highlightBadge) {
        updateHighlightBadge(offerPrice.highlightBadge, newState);
    }
};

/**
 * Update tariff information in offer price
 *
 * @param container
 * @param offerPrice
 * @param state
 * @param actions
 */
export const updateTariffInformation = (
    container: HTMLElement,
    offerPrice: OfferPriceProperties,
    state: StateProps,
    actions: DeviceDetailActionDispatchers,
): void => {
    const { offer, subscriptionId, salesChannel, isHardwareOnly, hasInsurance, hasSubscription } = state;
    const { promoPrice, buttons, tariffInfo, legalText, stairway } = offerPrice;
    const { tariffOverview } = window[ADDITIONAL_PAGE_OPTIONS];

    tariffInfo.subline.update({ content: getSubline(offer as Offer, isHardwareOnly, hasSubscription) });

    if (hasSubscription || hasInsurance) {
        promoPrice.tariff.update({
            ...getMonthlyPrice(state),
        });
    }

    buttons.primary.update({
        ...getPrimaryButtonContent(state, actions),
    });

    buttons.secondary.update({
        href: salesChannel ? tariffOverview[salesChannel] : '',
    });

    legalText.update({
        content: composeLegalText(offer as Offer),
    });

    stairway.update({
        content: composeStairwayText(state),
    });

    // Hide the stuff we don't want
    container.getElementsByClassName(CLASSNAME_LEGAL)[0].classList.toggle(CLASSNAME_HIDDEN, shouldHideTariffInformation(!!subscriptionId, isHardwareOnly));
    container.getElementsByClassName(CLASSNAME_STAIRWAY)[0].classList.toggle(CLASSNAME_HIDDEN, shouldHideTariffInformation(!!subscriptionId, isHardwareOnly));
    container.getElementsByClassName(CLASSNAME_PRICE)[0].lastElementChild?.classList.toggle(CLASSNAME_HIDDEN, shouldHideMonthlyPrice(!!subscriptionId, isHardwareOnly, hasInsurance));
    container.getElementsByClassName(CLASSNAME_BUTTON)[0].lastElementChild?.classList.toggle(CLASSNAME_HIDDEN, shouldHideTariffInformation(!!subscriptionId, isHardwareOnly));
    container.getElementsByClassName(OFFER_PRICE_INFORMATION_CLASSNAME)[0].classList.toggle(CLASSNAME_HIDDEN, shouldHideVvlLink(salesChannel!));
};

const updateHighlightBadge = (highlightBadge: HighlightBadge, { subscriptionId, isGigakombi, isHardwareOnly, salesChannel }: StateProps) => {
    const highlightBadgeProps = highlightBadge.getProperties();
    const highlightBadgeContent = getHighlightBadgeContent(subscriptionId, isHardwareOnly, isGigakombi, salesChannel!);

    const { text, tooltip, icon, color } = highlightBadgeContent || {};

    highlightBadge.update(removeUndefinedFieldsFromObject({
        content: text,
        hidden: !text,
        tooltip: {
            ...highlightBadgeProps.tooltip,
            stdHeadline: tooltip?.headline,
            txtContent: `<p>${tooltip?.text}</p>`,
            hidden: !tooltip,
        },
        icon,
        color,
    }));
    handleLegacyClickEventBindings();
};

export default mountOfferPriceComponents;
